import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CustomerOrders } from '../types';
import { RootState } from '../store';
import API from '../lib/API';
import { setCustomerOrders } from '../slices/boutiquesSlice';

import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import Table from 'react-bootstrap/Table';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import ItemAutocomplete from './ItemAutocomplete';

interface OrderItem {
    boutiqueItemId: number;
    quantity: number;
    salePrice: number;
}

interface CustomerOrderFormModalProps {
    show: boolean;
    handleClose: () => void;
    token: string;
    boutiqueId: number;
    order?: CustomerOrders;
}

const CustomerOrderFormModal: React.FC<CustomerOrderFormModalProps> = ({ 
    show, 
    handleClose, 
    token, 
    boutiqueId, 
    order 
}) => {
    const dispatch = useDispatch();
    const boutiqueItems = useSelector((state: RootState) => state.boutiques.boutiqueItems);
    
    const [clientName, setClientName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [status, setStatus] = useState('pending');
    const [items, setItems] = useState<OrderItem[]>([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // New item form state
    const [selectedItemId, setSelectedItemId] = useState<string>('');
    const [quantity, setQuantity] = useState('1');
    const [salePrice, setSalePrice] = useState('');

    const clearForm = () => {
        setClientName('');
        setCustomerPhone('');
        setStatus('pending');
        setItems([]);
        setSelectedItemId('');
        setQuantity('1');
        setSalePrice('');
        setError('');
    };

    const loadOrderIntoForm = (o: CustomerOrders) => {
        setClientName(o.clientName);
        setCustomerPhone(o.customerPhone || '');
        setStatus(o.status);
        setItems(Array.isArray(o.items) ? o.items : []);
        setSelectedItemId('');
        setQuantity('1');
        setSalePrice('');
        setError('');
    };

    useEffect(() => {
        if (!show) return;
        if (order) loadOrderIntoForm(order);
        else clearForm();
    }, [show, order]);

    const handleAddItem = () => {
        if (!selectedItemId) {
            setError('Please select an item');
            return;
        }

        const newItem: OrderItem = {
            boutiqueItemId: parseInt(selectedItemId),
            quantity: parseInt(quantity),
            salePrice: parseFloat(salePrice)
        };

        setItems([...items, newItem]);
        
        // Reset form
        setSelectedItemId('');
        setQuantity('1');
        setSalePrice('');
        setError('');
    };

    const handleRemoveItem = (index: number) => {
        setItems(items.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!clientName) {
            setError('Please enter a client name');
            return;
        }

        if (items.length === 0) {
            setError('Please add at least one item');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const data = {
                boutiqueId,
                clientName,
                customerPhone: customerPhone || null,
                status,
                items
            };

            if (order) {
                await API.updateCustomerOrder(token, order.customerOrderId, data);
            } else {
                await API.createCustomerOrder(token, data);
            }

            const updatedOrders = await API.getCustomerOrders(token, boutiqueId);
            dispatch(setCustomerOrders(updatedOrders));
            clearForm();
            handleClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!order) return;

        setLoading(true);
        setError('');

        try {
            await API.deleteCustomerOrder(token, order.customerOrderId);
            const updatedOrders = await API.getCustomerOrders(token, boutiqueId);
            dispatch(setCustomerOrders(updatedOrders));
            clearForm();
            handleClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{order ? 'Edit Customer Order' : 'New Customer Order'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label>Client Name</Form.Label>
                        <Form.Control
                            type="text"
                            value={clientName}
                            onChange={(e) => setClientName(e.target.value)}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Customer Phone</Form.Label>
                        <Form.Control
                            type="tel"
                            value={customerPhone}
                            onChange={(e) => setCustomerPhone(e.target.value)}
                            placeholder="Optional"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Status</Form.Label>
                        <Form.Select
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            required
                        >
                            <option value="pending">Pending</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                        </Form.Select>
                    </Form.Group>

                    <div className="mb-3">
                        <h5>Order Items</h5>
                        <Table striped bordered hover size="sm" className="mb-3">
                            <thead>
                                <tr>
                                    <th>Item</th>
                                    <th>Quantity</th>
                                    <th>Sale Price</th>
                                    <th>Total</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, index) => {
                                    const boutiqueItem = boutiqueItems.find(bi => bi.boutiqueItemId === item.boutiqueItemId);
                                    return (
                                        <tr key={index}>
                                            <td>{boutiqueItem?.name || 'Unknown Item'}</td>
                                            <td>{item.quantity}</td>
                                            <td>{item.salePrice.toFixed(2)}</td>
                                            <td>{(item.quantity * item.salePrice).toFixed(2)}</td>
                                            <td>
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => handleRemoveItem(index)}
                                                >
                                                    Remove
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {items.length === 0 && (
                                    <tr>
                                        <td colSpan={5} className="text-center">No items added</td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>

                        <div className="border rounded p-3 bg-light">
                            <h6>Add New Item</h6>
                            <Row className="g-2">
                                <Col md={4}>
                                    <ItemAutocomplete
                                        items={boutiqueItems}
                                        value={selectedItemId}
                                        onChange={(itemId) => setSelectedItemId(itemId)}
                                        required
                                    />
                                </Col>
                                <Col md={3}>
                                    <Form.Control
                                        type="number"
                                        min="1"
                                        value={quantity}
                                        onChange={(e) => setQuantity(e.target.value)}
                                        size="sm"
                                        placeholder="Quantity"
                                    />
                                </Col>
                                <Col md={3}>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        value={salePrice}
                                        onChange={(e) => setSalePrice(e.target.value)}
                                        size="sm"
                                        placeholder="Sale Price"
                                    />
                                </Col>
                                <Col md={2}>
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={handleAddItem}
                                        className="w-100"
                                    >
                                        Add
                                    </Button>
                                </Col>
                            </Row>
                        </div>
                    </div>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                {order && (
                    <Button 
                        variant="danger" 
                        onClick={handleDelete}
                        disabled={loading}
                        className="me-auto"
                    >
                        Delete
                    </Button>
                )}
                <Button variant="secondary" onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleSubmit}
                    disabled={loading}
                >
                    {loading ? 'Saving...' : 'Save'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CustomerOrderFormModal; 